import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface Category {
  id: string;
  name: string;
  description?: string;
}

interface CategoryTabsProps {
  categories: Category[];
  selectedCategory: string;
  onCategorySelect: (categoryId: string) => void;
}

export const CategoryTabs: React.FC<CategoryTabsProps> = ({
  categories,
  selectedCategory,
  onCategorySelect
}) => {
  const { darkMode } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [visibleCategories, setVisibleCategories] = useState<Category[]>([]);
  const [overflowCategories, setOverflowCategories] = useState<Category[]>([]);
  
  // Handle window resize to determine which categories to show directly vs in dropdown
  useEffect(() => {
    const handleResize = () => {
      // Simple logic: on smaller screens show fewer categories
      const breakpoint = window.innerWidth < 768 ? 2 : window.innerWidth < 1024 ? 4 : 6;
      setVisibleCategories(categories.slice(0, breakpoint));
      setOverflowCategories(categories.slice(breakpoint));
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [categories]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className={`category-tabs mb-6 ${
      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    } rounded-t-lg border border-b-0 transition-colors duration-200`}>
      <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <nav className="-mb-px flex items-center">
          <button
            onClick={() => onCategorySelect('all')}
            className={`whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm transition-colors ${
              selectedCategory === 'all'
                ? 'border-blue-500 text-blue-500'
                : darkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All Personas
          </button>
          
          {visibleCategories.map(category => (
            <button
              key={category.id}
              onClick={() => onCategorySelect(category.id)}
              className={`whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm transition-colors ${
                selectedCategory === category.id
                  ? 'border-blue-500 text-blue-500'
                  : darkMode
                    ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              title={category.description}
            >
              {category.name}
            </button>
          ))}
          
          {overflowCategories.length > 0 && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className={`whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm flex items-center ${
                  overflowCategories.some(c => c.id === selectedCategory)
                    ? 'border-blue-500 text-blue-500'
                    : darkMode
                      ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                More
                <svg 
                  className={`ml-1 h-4 w-4 transition-transform ${showDropdown ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showDropdown && (
                <div className={`absolute left-0 mt-1 w-48 ${
                  darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                } rounded-md shadow-lg z-10 border`}>
                  <div className="py-1">
                    {overflowCategories.map(category => (
                      <button
                        key={category.id}
                        onClick={() => {
                          onCategorySelect(category.id);
                          setShowDropdown(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          selectedCategory === category.id
                            ? darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-700'
                            : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        title={category.description}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </nav>
      </div>
    </div>
  );
};
